import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Box, Checkbox } from '@mui/material';
import { API_BASE } from '../../config/constants';

interface Doc {
    id?: string | number;
    document_id?: string | number;
    url: string;
    name: string;
    contentType: string;
    size: number;
    lastModified: string;
    etag: string;
}

interface CompanyDocsFormProps {
    open: boolean;
    onClose: () => void;
    companyId: string | number | null;
    companyName?: string | null;
    onFileDrop?: (file: File, companyId: string | number) => Promise<void> | void;
    canUpload?: boolean;
}

export default function CompanyDocsForm({ open, onClose, companyId, companyName = null, onFileDrop, canUpload = false }: CompanyDocsFormProps) {
    const [docs, setDocs] = useState<Doc[]>([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [docsError, setDocsError] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState('');
    const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
    const [deletingDocs, setDeletingDocs] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const fetchCompanyDocs = async (currentCompanyId: string | number) => {
        setDocsLoading(true);
        setDocsError(null);
        setDocs([]);

        try {
            const response = await fetch(`${API_BASE}/api/users/get-company-docs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ company_id: currentCompanyId }),
            });

            if (!response.ok) throw new Error('Failed to fetch documents');

            const result = await response.json();
            if (result.success && Array.isArray(result.data)) {
                setDocs(result.data);
                setSelectedDocs(new Set());
            } else {
                setDocs([]);
                setDocsError(result.message || 'No documents found');
                setSelectedDocs(new Set());
            }
        } catch (err: any) {
            setDocsError(err.message || 'Error fetching documents');
            setDocs([]);
            setSelectedDocs(new Set());
        } finally {
            setDocsLoading(false);
        }
    };

    useEffect(() => {
        if (!open || companyId === null) return;
        fetchCompanyDocs(companyId);
    }, [open, companyId]);

    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0 || companyId === null) return;
        const file = files[0];
        setSelectedFileName(file.name);

        if (onFileDrop) {
            try {
                await onFileDrop(file, companyId);
                await fetchCompanyDocs(companyId);
            } catch (err: any) {
                setDocsError(err?.message || 'Failed to add file');
            }
        }
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
        await handleFiles(event.dataTransfer.files);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragOver(false);
    };

    const toggleDocSelection = (docUrl: string) => {
        setSelectedDocs((prev) => {
            const next = new Set(prev);
            if (next.has(docUrl)) {
                next.delete(docUrl);
            } else {
                next.add(docUrl);
            }
            return next;
        });
    };

    const handleDeleteSelected = async () => {
        if (!companyId || selectedDocs.size === 0) return;

        setDeletingDocs(true);
        setDocsError(null);

        try {
            const docsToDelete = docs.filter((doc) => selectedDocs.has(doc.url));
            console.log('[CompanyDocsForm] Deleting selected docs', {
                companyId,
                count: docsToDelete.length,
                docs: docsToDelete.map((d) => ({ id: d.id ?? d.document_id, name: d.name, url: d.url })),
            });

            for (const doc of docsToDelete) {
                const docId = doc.id ?? doc.document_id;
                const query = new URLSearchParams({
                    container_name: 'docs',
                    directory_name: `companies/${companyId}`,
                    doc_url: String(doc.url),
                    file_name: String(doc.name),
                });

                const payload: Record<string, string> = {
                    container_name: 'docs',
                    directory_name: `companies/${companyId}`,
                    doc_url: String(doc.url),
                    file_name: String(doc.name),
                };

                if (docId !== undefined && docId !== null) {
                    query.set('document_id', String(docId));
                    payload.document_id = String(docId);
                }

                const response = await fetch(`${API_BASE}/api/users/delete-document-by-id?${query.toString()}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                const responseText = await response.text();
                console.log('[CompanyDocsForm] delete-document-by-id response', {
                    name: doc.name,
                    status: response.status,
                    ok: response.ok,
                    responseText,
                });

                if (!response.ok) {
                    throw new Error(responseText || `Failed to delete ${doc.name}`);
                }
            }

            await fetchCompanyDocs(companyId);
        } catch (err: any) {
            setDocsError(err?.message || 'Failed to delete selected documents');
        } finally {
            setDeletingDocs(false);
        }
    };

    const getDocVisual = (doc: Doc) => {
        const extension = String(doc.name || '').split('.').pop()?.toLowerCase() || '';
        const contentType = String(doc.contentType || '').toLowerCase();

        const isPdf = extension === 'pdf' || contentType.includes('pdf');
        const isDocx = extension === 'docx' || contentType.includes('wordprocessingml');

        if (isPdf) {
            return {
                label: 'PDF',
                color: '#D32F2F',
                bg: '#FDECEC',
            };
        }

        if (isDocx) {
            return {
                label: 'DOCX',
                color: '#2B579A',
                bg: '#E8F0FE',
            };
        }

        return {
            label: extension ? extension.toUpperCase() : 'FILE',
            color: '#616161',
            bg: '#F5F5F5',
        };
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Documents for {companyName ?? `Company ${companyId}`}</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        border: '2px dashed',
                        borderColor: isDragOver ? '#1976d2' : '#c4c4c4',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                        backgroundColor: isDragOver ? 'rgba(25, 118, 210, 0.08)' : '#fafafa',
                        transition: 'all 0.2s ease',
                        mb: 2,
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                >
                    <div style={{ fontSize: '14px', marginBottom: 8 }}>
                        Drag and drop a file here
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        or click Add to choose a file
                    </div>
                    {selectedFileName && (
                        <div style={{ marginTop: 8, fontSize: '12px', color: '#1976d2' }}>
                            Selected: {selectedFileName}
                        </div>
                    )}
                </Box>

                {docsLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {docsError && <Box sx={{ color: '#d32f2f', py: 2 }}>{docsError}</Box>}

                {!docsLoading && !docsError && docs.length === 0 && (
                    <Box sx={{ py: 2, color: '#666' }}>No documents found for this company.</Box>
                )}

                {!docsLoading && docs.length > 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, py: 2 }}>
                        {docs.map((doc, idx) => {
                            const isImage = doc.contentType?.startsWith('image/');
                            return (
                                <Box
                                    key={idx}
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        },
                                    }}
                                    onClick={() => window.open(doc.url, '_blank')}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            zIndex: 1,
                                            backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                            borderRadius: '50%',
                                        }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                    >
                                        <Checkbox
                                            size='small'
                                            checked={selectedDocs.has(doc.url)}
                                            onChange={() => toggleDocSelection(doc.url)}
                                        />
                                    </Box>
                                    {isImage ? (
                                        <img
                                            src={doc.url}
                                            alt={doc.name}
                                            style={{
                                                width: '100%',
                                                height: '150px',
                                                objectFit: 'cover',
                                                display: 'block',
                                            }}
                                        />
                                    ) : (
                                        (() => {
                                            const visual = getDocVisual(doc);
                                            return (
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: '150px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: visual.bg,
                                                        textAlign: 'center',
                                                        padding: 1,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            border: `2px solid ${visual.color}`,
                                                            color: visual.color,
                                                            borderRadius: 1,
                                                            px: 1.5,
                                                            py: 0.5,
                                                            fontSize: '12px',
                                                            fontWeight: 700,
                                                            letterSpacing: '0.4px',
                                                            backgroundColor: '#fff',
                                                        }}
                                                    >
                                                        {visual.label}
                                                    </Box>
                                                </Box>
                                            );
                                        })()
                                    )}
                                    <Box sx={{ p: 1, fontSize: '12px', maxHeight: '60px', overflow: 'hidden' }}>
                                        <strong>{doc.name}</strong>
                                        <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                                            {(doc.size / 1024).toFixed(1)} KB
                                        </div>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <>
                    <input
                        ref={fileInputRef}
                        type='file'
                        style={{ display: 'none' }}
                        onChange={(event) => handleFiles(event.target.files)}
                    />
                    <Button onClick={() => fileInputRef.current?.click()} variant='outlined'>
                        Add
                    </Button>
                    <Button
                        onClick={handleDeleteSelected}
                        variant='outlined'
                        color='error'
                        disabled={selectedDocs.size === 0 || deletingDocs}
                    >
                        {deletingDocs ? 'Deleting...' : `Delete (${selectedDocs.size})`}
                    </Button>
                </>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
